// Flat endpoint wrappers (one function per endpoint), mirroring
// global_100seconds's services/Api.jsx. Each returns the axios promise;
// callers read `.data`.
import { API_URLS } from "../common/constants";
import type { Admin, Role } from "../config/Auth";
import { Request } from "../config/Request";

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  admin: Admin;
}

// ---- auth ----
export const adminLogin = (username: string, password: string) =>
  Request.post<TokenResponse>(API_URLS.LOGIN, { username, password }, { _bypass: true });
export const adminRefresh = () => Request.post<TokenResponse>(API_URLS.REFRESH, null, { _bypass: true });
export const adminLogout = () => Request.post(API_URLS.LOGOUT, null, { _bypass: true });
export const getAdminMe = () => Request.get<Admin>(API_URLS.ME);

// ---- team (admin only) ----
export const listTeam = () => Request.get<Admin[]>(API_URLS.TEAM);
export const createTeamMember = (body: {
  username: string;
  email: string;
  password: string;
  display_name?: string | null;
  role: Role;
}) => Request.post<Admin>(API_URLS.TEAM, body);
export const updateTeamMember = (
  id: string,
  body: Partial<{ email: string; display_name: string | null; role: Role; is_active: boolean; password: string }>,
) => Request.patch<Admin>(API_URLS.TEAM_MEMBER(id), body);
export const resetTeamMemberPassword = (id: string) =>
  Request.post<{ public_id: string; username: string; new_password: string }>(API_URLS.TEAM_RESET_PW(id));

// ---- cms content ----
export interface PageSummary {
  slug: string;
  status: string;
  has_unpublished_changes: boolean;
  updated_at: string | null;
  draft_updated_by: number | null;
  published_at: string | null;
  published_by: number | null;
}
export interface PageContent {
  slug: string;
  state: string;
  data: Record<string, unknown> | null;
}
export interface RevisionChange {
  path: string;
  before: string | null;
  after: string | null;
}
export interface Revision {
  id: number;
  page_slug: string;
  kind: string;
  actor_admin_id: number | null;
  actor: string | null;
  note: string;
  created_at: string;
  changes: RevisionChange[];
}

export const listPages = () => Request.get<PageSummary[]>(API_URLS.CMS_PAGES);
export const getPageContent = (slug: string, state: "draft" | "published") =>
  Request.get<PageContent>(`${API_URLS.CMS_PAGE(slug)}?state=${state}`);
export const saveDraft = (slug: string, data: Record<string, unknown>, note = "") =>
  Request.put<PageSummary>(API_URLS.CMS_PAGE(slug), { data, note });
export const publishPage = (slug: string) => Request.post<PageSummary>(API_URLS.CMS_PUBLISH(slug));
export const listRevisions = (slug: string) => Request.get<Revision[]>(API_URLS.CMS_REVISIONS(slug));
export const getRevision = (slug: string, id: number) =>
  Request.get<Revision & { data: Record<string, unknown> }>(API_URLS.CMS_REVISION(slug, id));
export const revertTo = (slug: string, id: number) => Request.post<PageSummary>(API_URLS.CMS_REVERT(slug, id));
